import { z } from 'zod/v4';

import { DurationRuntype } from '../activity/activityBackendTypes';
import { ActivityConfigurationSummaryRuntype, DriverStatusDetailsRuntype } from '../activityConfiguration';
import { ExchangedMonetaryValueBackendConvertedMVOnlyRuntype } from '../backendTypes';
import { ExpenseConfigurationSummaryRuntype } from '../expenses/expenseConfigurationBackendTypes';
import { ManufacturingAssemblyDetailsRuntype } from '../manufacturingAssemblyDetails/manufacturingAssemblyDetailsBackendTypes';

const ManufacturingScenarioDetailsRuntype = {
    id: z.string(),
    manufacturing_assembly_details: z.string(),
    name: z.string(),
    notes: z.string().nullable().optional(),
    batch_sizes: z.array(z.number()),
    sourcing_scenario: z.string().nullable(),
    statuses: z.array(DriverStatusDetailsRuntype),
    sites: z.array(z.string()),
};

export const ManufacturingScenarioRuntype = z.object(ManufacturingScenarioDetailsRuntype);

export const ManufacturingScenarioBulkPostRuntype = z.object({
    manufacturing_assembly_details: z.string(),
    name: z.string(),
    notes: z.string().nullable(),
    batch_sizes: z.array(z.number()),
    sourcing_scenario: z.string().nullable(),
    from_template: z.string().nullable(),
});

export const ManufacturingScenarioBulkPostResponseRuntype = ManufacturingScenarioBulkPostRuntype.omit({
    manufacturing_assembly_details: true,
    from_template: true,
});

export interface ManufacturingScenarioBulkPostResponseDTO extends z.infer<
    typeof ManufacturingScenarioBulkPostResponseRuntype
> {}

export interface ManufacturingScenarioBulkPostDTO extends z.infer<typeof ManufacturingScenarioBulkPostRuntype> {}

export interface ManufacturingScenarioDTO extends z.infer<typeof ManufacturingScenarioRuntype> {}
export const ManufacturingScenarioPostRuntype = ManufacturingScenarioRuntype.omit({
    id: true,
    statuses: true,
    manufacturing_assembly_details: true,
    sites: true,
}).extend({ assembly: z.string() });
export interface ManufacturingScenarioPostDTO extends z.infer<typeof ManufacturingScenarioPostRuntype> {}

export const ManufacturingScenarioDuplicatePostRuntype = z.object({
    name: z.string(),
    batch_sizes: z.array(z.number()),
    sourcing_scenario: z.string().nullable(),
});

export interface ManufacturingScenarioDuplicatePostDTO extends z.infer<
    typeof ManufacturingScenarioDuplicatePostRuntype
> {}

export const ManufacturingScenarioPatchRuntype = z
    .object({
        ...ManufacturingScenarioDetailsRuntype,
        ordered_activity_configurations: z.array(z.string()),
        ordered_expense_configurations: z.array(z.string()),
    })
    .partial()
    .omit({ sites: true })
    .and(z.object({ id: z.string() }));
export interface ManufacturingScenarioPatchDTO extends z.infer<typeof ManufacturingScenarioPatchRuntype> {}

const BatchSizeBreakdownRuntype = z.object({
    total_batch_cost: ExchangedMonetaryValueBackendConvertedMVOnlyRuntype,
    total_batch_time: DurationRuntype,
    total_effective_cost_per_batch: ExchangedMonetaryValueBackendConvertedMVOnlyRuntype,
    total_effective_cost_per_unit: ExchangedMonetaryValueBackendConvertedMVOnlyRuntype,
    total_project_cost: ExchangedMonetaryValueBackendConvertedMVOnlyRuntype,
    total_unit_cost: ExchangedMonetaryValueBackendConvertedMVOnlyRuntype,
    total_unit_time: DurationRuntype,
    total_project_time: DurationRuntype,
});

export interface BatchSizeBreakdown extends z.infer<typeof BatchSizeBreakdownRuntype> {}

const ManufacturingScenarioBatchSizeBreakdownsRuntype = z.record(z.string(), BatchSizeBreakdownRuntype);
export type ManufacturingScenarioBatchSizeBreakdownsDTO = z.infer<
    typeof ManufacturingScenarioBatchSizeBreakdownsRuntype
>;

export const TemplateInstanceSummaryRuntype = z.object({
    id: z.string(),
    template_name: z.string(),
    rank: z.number(),
});

export type TemplateInstanceSummaryDTO = z.infer<typeof TemplateInstanceSummaryRuntype>;

export const ManufacturingScenarioFullSummaryRuntype = z.object({
    activity_configuration_summaries: z.object({
        items: z.array(ActivityConfigurationSummaryRuntype),
    }),
    expense_configuration_summaries: z.object({
        items: z.array(ExpenseConfigurationSummaryRuntype),
    }),
    template_instances: z.array(TemplateInstanceSummaryRuntype),
    breakdown_per_batch_size: ManufacturingScenarioBatchSizeBreakdownsRuntype,
    manufacturing_assembly_details: ManufacturingAssemblyDetailsRuntype,
    manufacturing_scenario: ManufacturingScenarioRuntype,
    manufacturing_lead_time: z.number().nullable(),
});

export interface ManufacturingScenarioFullSummaryDTO extends z.infer<typeof ManufacturingScenarioFullSummaryRuntype> {}

export const ManufacturingScenarioLeadTimeRuntype = z.object({
    manufacturing_scenario_id: z.string(),
    manufacturing_lead_time: z.number().nullable(),
});

export interface ManufacturingScenarioLeadTimeDTO extends z.infer<typeof ManufacturingScenarioLeadTimeRuntype> {}

export const ManufacturingScenarioLeadTimesResponseRuntype = z.object({
    items: z.array(ManufacturingScenarioLeadTimeRuntype),
});

/* the customer portal information is available to the customer users.
It needs to be different from the regular Template DTO so we do not 
expose sensistve EMS data to the customer user. 
*/
const TemplateDTOForCustomerPortalRuntype = z.object({
    id: z.string(),
    name: z.string(),
});

export interface TemplateDTOForCustomerPortal extends z.infer<typeof TemplateDTOForCustomerPortalRuntype> {}

export const CustomerPortalManufacturingSettingsPatchRuntype = z.object({
    templates: z.array(z.string()),
});

export interface CustomerPortalManufacturingSettingsPatchDTO extends z.infer<
    typeof CustomerPortalManufacturingSettingsPatchRuntype
> {}

export const CustomerPortalManufacturingSettings = z.object({
    templates: z.array(TemplateDTOForCustomerPortalRuntype),
});

export interface CustomerPortalManufacturingSettingsDTO extends z.infer<typeof CustomerPortalManufacturingSettings> {}
